import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Container,
  IconButton,
  Paper,
  Typography,
  AppBar,
  Toolbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PackagingSolution, Product, BRAND_COLORS, FONTS } from '../types/index.ts';
import BoxVisualization from './BoxVisualization.tsx';
import FillLine from './FillLine.tsx';
import FillCube from './FillCube.tsx';
import { decodeShareData, sanitizeSharedPayload, SanitizedSharePayload } from '../services/shareCodec.ts';

interface SharedResultsProps {
  products?: Product | Product[];
  sharedSolutions?: PackagingSolution[];
}

const getOrientationLabel = (orientation: string) => {
  switch (orientation) {
    case 'standard':
      return 'Standard';
    case 'tilted':
      return 'Tilted';
    case 'length-rotated':
      return 'Length Rotated';
    case 'width-rotated':
      return 'Width Rotated';
    case 'flipped':
      return 'Flipped';
    case 'combined-rotation':
      return 'Combined Rotation';
    default:
      return orientation;
  }
};

const SharedResults: React.FC<SharedResultsProps> = ({ products, sharedSolutions }) => {
  // Décodage SYNCHRONE (pas d'useEffect) : pas de flash « Invalid share link »
  // au premier rendu. Les données de l'URL ne sont jamais fiables : tout passe
  // par sanitizeSharedPayload (formes, bornes, valeurs finies).
  const shared = useMemo((): SanitizedSharePayload | null => {
    if (products && sharedSolutions) {
      return sanitizeSharedPayload({ products, solutions: sharedSolutions });
    }
    const sharedDataParam = new URLSearchParams(window.location.search).get('shared');
    if (!sharedDataParam) return null;
    return sanitizeSharedPayload(decodeShareData(sharedDataParam));
  }, [products, sharedSolutions]);

  // Utilisation réelle pour la quantité demandée (mêmes règles que ResultsDisplay).
  const calculateActualSpaceUtilization = useMemo(() => {
    return (solution: PackagingSolution): number => {
      if (!shared) return 0;
      const quantity =
        solution.products && solution.products.length > 1
          ? solution.totalProductsQuantity ||
            shared.products.reduce((sum, p) => sum + p.quantity, 0)
          : shared.products[0].quantity;
      const totalActualProducts = quantity;
      const maxPossibleProducts = solution.totalBoxesNeeded * solution.productsPerBox;
      if (maxPossibleProducts <= 0) return solution.spaceUtilization;
      return Math.min(
        solution.spaceUtilization,
        (totalActualProducts / maxPossibleProducts) * solution.spaceUtilization
      );
    };
  }, [shared]);

  // Tri affiché : même règle que l'app (minimum de caisses, puis utilisation).
  const displayedSolutions = useMemo(() => {
    if (!shared) return [];
    return [...shared.solutions].sort(
      (a, b) =>
        a.totalBoxesNeeded - b.totalBoxesNeeded ||
        calculateActualSpaceUtilization(b) - calculateActualSpaceUtilization(a)
    );
  }, [shared, calculateActualSpaceUtilization]);

  // null = défaut (première solution dépliée), '' = tout replié, sinon boxId.
  const [expandedSolution, setExpandedSolution] = useState<string | null>(null);
  const effectiveExpanded =
    expandedSolution === null ? displayedSolutions[0]?.boxId ?? '' : expandedSolution;

  if (!shared || displayedSolutions.length === 0) {
    return (
      <Container maxWidth="lg">
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            m: 4,
            backgroundColor: BRAND_COLORS.cardStock,
            borderLeft: `4px solid ${BRAND_COLORS.amber}`,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: BRAND_COLORS.ink }}>
            Invalid share link
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            This share link is invalid or has expired.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const productSummary = shared.products.map((p, idx) => (
    <Box component="span" key={p.id}>
      {idx > 0 && '   ·   '}
      {p.quantity}× {p.dimensions.length}×{p.dimensions.width}×{p.dimensions.height} {p.unit} (L×W×H)
    </Box>
  ));

  return (
    <Box>
      {/* Console header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ backgroundColor: BRAND_COLORS.dockBlue, borderBottom: `2px solid ${BRAND_COLORS.ink}` }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <img src="/boxfinder-icon.svg" alt="Box Finder logo" style={{ height: 30, width: 30 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontFamily: FONTS.display, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Box Finder
          </Typography>
          <Typography
            component="div"
            sx={{ fontFamily: FONTS.mono, fontSize: 12, color: 'rgba(255,255,255,0.7)', display: { xs: 'none', sm: 'block' } }}
          >
            Recommended packaging
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ color: BRAND_COLORS.ink, mb: 1 }}>
          Selected packaging solutions
        </Typography>

        <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
          Here are the recommended packaging options for your products, selected to optimize space and
          minimize packaging costs.
        </Typography>

        {/* Order summary */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: BRAND_COLORS.cardStock }}>
          <Typography variant="overline" component="div" sx={{ color: BRAND_COLORS.slateText }}>
            Order summary
          </Typography>
          <Typography component="div" sx={{ fontFamily: FONTS.mono, fontSize: 14, mt: 0.5 }}>
            {productSummary}
          </Typography>
        </Paper>

        <Typography variant="overline" component="div" sx={{ color: BRAND_COLORS.slateText, mb: 1, display: 'block' }}>
          Recommended solutions
        </Typography>

        {displayedSolutions.map((solution, index) => {
          const util = calculateActualSpaceUtilization(solution);
          const isPrimary = index === 0;
          const isExpanded = effectiveExpanded === solution.boxId;
          return (
            <Card key={solution.boxId} sx={{ mb: 2, borderWidth: isPrimary ? 2 : 1.5, overflow: 'hidden' }}>
              {isPrimary && <Box sx={{ height: 6, bgcolor: BRAND_COLORS.dockBlue }} />}
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Box
                  onClick={() => setExpandedSolution(isExpanded ? '' : solution.boxId)}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, cursor: 'pointer' }}
                >
                  <Box sx={{ flexShrink: 0, lineHeight: 0, mt: 0.25 }}>
                    <FillCube utilization={util} size={isPrimary ? 46 : 36} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.ink, lineHeight: 1.2 }}>
                      {solution.boxName}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{ fontFamily: FONTS.mono, fontSize: 12, color: BRAND_COLORS.slateText, mt: 0.25 }}
                    >
                      {getOrientationLabel(solution.orientation)} · {solution.totalBoxesNeeded} box
                      {solution.totalBoxesNeeded > 1 ? 'es' : ''} · {solution.productsPerBox}/box
                    </Typography>
                  </Box>
                  {solution.price && (
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="overline" component="div" sx={{ color: BRAND_COLORS.slateText }}>
                        Price
                      </Typography>
                      <Typography
                        component="div"
                        sx={{ fontFamily: FONTS.mono, fontWeight: 600, fontSize: 20, color: BRAND_COLORS.ink }}
                      >
                        {solution.price}
                      </Typography>
                    </Box>
                  )}
                  <IconButton size="small" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <FillLine utilization={util} variant={isPrimary ? 'primary' : 'compact'} />

                {isExpanded && (
                  <Box sx={{ mt: 2 }}>
                    <BoxVisualization
                      product={solution.products && solution.products.length > 0 ? solution.products : shared.products}
                      solution={solution}
                      unitType={shared.products[0].unit === 'in' ? 'inch' : 'cm'}
                    />
                  </Box>
                )}
              </Box>
            </Card>
          );
        })}

        <Box sx={{ mt: 4, pt: 2, borderTop: `1px solid rgba(23,34,46,0.15)` }}>
          <Typography component="p" sx={{ fontFamily: FONTS.mono, fontSize: 12, color: BRAND_COLORS.slateText }}>
            Generated by Box Finder · Killian Borowiec · {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SharedResults;
